import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, Button, Col, Row, Select, Space, Tag, Tooltip, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import ImportadorExcelGenerico from "../../../../../Components/ImportadorExcelGenerico";
import { SelectItem } from "../../../../../Components/Items";
import SelectClientesItem from "../../../../../Components/SelectClientesItem";
import ModalProgreso from "../../../../../Components/ModalProgreso";
import SeguidorProcesoMasivo from "../../../../../Components/seguirProcesoMasivo";
import ResultadosLibrosDigitales from "./ResultadosLibrosDigitales";
import { descargarVistaPrevia } from "./vistaPreviaExcel";
import {
  construirIndicesAlumnos,
  buscarAlumnoPorMatricula,
} from "../ImportarVentasLibros/coincidenciaMaterial";
import {
  NOMBRE_PLANTILLA,
  construirPlantilla,
} from "./plantillaVentasLibrosDigitales";
import {
  ESTADOS,
  analizarArchivo,
  colorEstado,
  detectarColumnas,
  etiquetaEstado,
  lecturaDeFilas,
} from "./analizarLibrosDigitales";

// La resolución de códigos es sólo lectura y su costo lo pone una lectura del historial de
// los libros que es casi igual con 300 códigos que con 1000: bloques grandes la hacen una o
// dos veces por archivo. Mismo tope que MAX_LOTES_POR_CONSULTA del backend.
const LOTES_POR_CONSULTA = 1000;

// La importación corre en el servidor (lanzarProcesoMasivo) con este nombre de operación.
const OPERACION = "importarLibrosDigitales";

const partirEnBloques = (lista, tamano) => {
  const bloques = [];
  for (let i = 0; i < lista.length; i += tamano) {
    bloques.push(lista.slice(i, i + tamano));
  }
  return bloques;
};

const instrucciones = [
  <>
    Un renglón por <b>código</b>, no por alumno. Si un alumno tiene dos libros, va dos veces
    repitiendo su matrícula: sus dos códigos se juntan en <b>una sola venta</b> y recibe un
    solo correo con los dos.
  </>,
  <>
    El <b>número de lote</b> es el código que el alumno ya tiene en la mano. Con él se busca
    la venta: si el código <b>todavía no se ha vendido</b> se le crea la venta al alumno y se
    le entrega ese código exacto; si <b>ya se vendió al cliente de origen</b>, cada matrícula
    termina con <b>su propia venta</b>.
  </>,
  <>
    Si esa venta sólo trae los códigos del alumno, se le cambia el cliente a la venta
    completa. Si trae códigos de otros alumnos, los del alumno <b>se separan a una venta
    nueva a su nombre</b> (con su propio folio de remisión) con su parte del cobro; la
    venta original se queda con lo demás.
  </>,
  <>
    Elige arriba el <b>cliente de origen</b> (normalmente Público en General, el que se usó en
    las ventas de mostrador) <b>antes</b> de subir el archivo: sin él no se puede saber qué
    ventas se pueden reasignar.
  </>,
  <>
    <b>No se toca</b> la venta que sea de otro cliente, la que ya esté timbrada y la que ya
    haya entrado en una Factura Global. Tampoco se separa una venta con cobro parcial,
    cobrada con varias formas de pago o con retenciones. Todas ésas salen en el Excel de
    resultados con su folio y su cliente para revisarlas a mano.
  </>,
  <>
    Antes de importar puedes <b>descargar la vista previa en Excel</b> (botón junto al resumen)
    para revisarla con calma: trae lo que va a pasar con cada código, una hoja con los que hay
    que revisar a mano y un resumen.
  </>,
  <>
    El proceso es <b>repetible</b>: si vuelves a subir el mismo archivo, las ventas que ya
    quedaron a nombre del alumno sólo reciben otra vez el correo, no se duplican.
  </>,
  <>
    La importación <b>corre en el servidor</b>, un renglón tras otro sin saturarlo, y puede
    tardar varios minutos. Puedes salir de la pantalla o cerrar la pestaña: sigue sola, y al
    volver a entrar ves su avance. Con <b>Detener</b> termina los renglones en curso y se para;
    al volver a subir el archivo sólo se procesa lo que faltó. Al terminar se muestra el
    detalle fila por fila y se puede descargar en Excel.
  </>,
];

class ImportarVentasLibrosDigitales extends Component {
  state = {
    catalogos: null,
    clienteOrigenId: undefined,
    proyectoId: undefined,
    bancoId: undefined,
    formaDePagoId: undefined,
    resumen: null,
    // Los `datos` de cada fila de la vista previa, para descargarla en Excel y revisarla.
    vistaPrevia: [],
    modalVisible: false,
    resultado: null,
    // Importación en curso en el servidor.
    corridaId: null,
    progresoVisible: false,
    progreso: null,
    deteniendo: false,
    iniciando: false,
    // La última corrida del usuario, para ofrecer sus resultados al volver a la pantalla.
    ultimaCorrida: null,
    verCorridaAnterior: false,
  };

  componentDidMount() {
    this.seguidor = new SeguidorProcesoMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      onProgreso: (progreso) => {
        if (progreso.operacion && progreso.operacion !== OPERACION) return;
        this.setState({ progreso });
      },
      onFin: () => this.alTerminarCorrida(),
      onError: (mensaje) => {
        message.error(mensaje || "La importación se interrumpió");
        this.alTerminarCorrida();
      },
    });
    this.seguidor.escuchar();
    this.revisarCorridaAnterior();
  }

  componentWillUnmount() {
    this.desmontado = true;
    if (this.seguidor) this.seguidor.detener();
  }

  /**
   * Al entrar: si el usuario tiene una importación corriendo (salió de la pantalla o cerró la
   * pestaña a media corrida) se vuelve a pegar a ella; si ya terminó, se ofrecen sus
   * resultados.
   */
  revisarCorridaAnterior = async () => {
    let corrida = null;
    try {
      const res = await axios.post(
        "ventasLibrosDigitales/estadoImportacionVentasLibrosDigitales",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      );
      corrida = res.data.success === 2 ? res.data.corrida : null;
    } catch (err) {
      return;
    }

    if (!corrida || this.desmontado) return;

    if (!corrida.terminado) {
      const avance = await this.seguidor.consultarEnCurso();
      if (avance && avance.operacion === OPERACION) {
        this.setState({
          corridaId: corrida.corridaId,
          progresoVisible: true,
          progreso: avance,
        });
        return;
      }
      // Lo que esté corriendo es otra operación masiva (o la importación murió con el
      // servidor): no es nuestro modal. Se vuelve a escuchar en limpio.
      this.seguidor.detener();
      this.seguidor.escuchar();
    }

    this.setState({ ultimaCorrida: corrida });
  };

  /**
   * Arranca la importación en el servidor. La llama el importador genérico con el mismo
   * payload de siempre (prop `enviarImportacion`).
   */
  enviarImportacion = async (payload) => {
    if (this.state.iniciando || this.state.progresoVisible) return;
    this.setState({ iniciando: true });

    let res;
    try {
      res = await axios.post(
        "ventasLibrosDigitales/iniciarImportacionVentasLibrosDigitales",
        payload,
        { headers: this.props.headersToken },
      );
    } catch (err) {
      this.setState({ iniciando: false });
      message.error(err.response?.data?.message || "No se pudo iniciar la importación");
      return;
    }

    if (res.data.success !== 2) {
      this.setState({ iniciando: false });
      message.error(res.data.message || "No se pudo iniciar la importación");
      return;
    }

    this.setState({
      iniciando: false,
      corridaId: res.data.corridaId,
      progresoVisible: true,
      deteniendo: false,
      ultimaCorrida: null,
      progreso: { procesados: 0, total: res.data.total ?? null, detalle: null },
    });
    this.seguidor.seguir(res.data.procesoId);
  };

  alTerminarCorrida = async () => {
    const { corridaId } = this.state;
    this.setState({ progresoVisible: false, deteniendo: false, progreso: null });
    if (corridaId) await this.cargarResultado(corridaId);
  };

  cargarResultado = async (corridaId) => {
    try {
      const res = await axios.post(
        "ventasLibrosDigitales/resultadoImportacionVentasLibrosDigitales",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron leer los resultados");
        return;
      }
      if (this.desmontado) return;
      this.setState({ resultado: res.data, modalVisible: true, corridaId: null });
    } catch (err) {
      message.error("No se pudieron leer los resultados de la importación");
    }
  };

  detenerImportacion = async () => {
    const { corridaId } = this.state;
    if (!corridaId) return;
    this.setState({ deteniendo: true });
    try {
      const res = await axios.post(
        "ventasLibrosDigitales/detenerImportacionVentasLibrosDigitales",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success === 2) {
        message.info("Se detendrá al terminar los renglones en curso");
      } else {
        this.setState({ deteniendo: false });
        message.warning(res.data.message || "No se pudo detener");
      }
    } catch (err) {
      this.setState({ deteniendo: false });
      message.error("No se pudo detener la importación");
    }
  };

  // Sale de la pantalla: la importación sigue en el servidor y al volver se retoma el avance.
  salirSinDetener = () => {
    message.info(
      "La importación sigue en el servidor. Vuelve a esta pantalla para ver su avance y sus resultados.",
    );
    this.setState({ progresoVisible: false });
    this.irAtras();
  };

  irAtras = () => {
    if (this.props.location?.state?.volverA) {
      this.props.history.replace(this.props.location.state.volverA);
    } else {
      this.props.history.goBack();
    }
  };

  // El importador guarda los catálogos en su propio estado; este hook es el único punto
  // donde también llegan aquí, que es donde se necesitan para los selects.
  prepararCatalogos = (resData) => {
    this.setState({ catalogos: resData });
    return resData;
  };

  /**
   * Pregunta al backend, por cada (código, alumno) del archivo, si el código sigue vivo y de
   * quién es la venta que se lo llevó.
   *
   * El resultado es POSICIONAL respecto a `lecturas`: el mismo código puede venir dos veces
   * con alumnos distintos y cada fila necesita su propio veredicto.
   *
   * @returns {Promise<Array|null>} null si la consulta falló (la pantalla lo reporta)
   */
  resolverLotes = async (lecturas) => {
    const pendientes = [];
    lecturas.forEach((lectura, posicion) => {
      if (!lectura.alumno || !lectura.numeroLote) return;
      pendientes.push({
        posicion,
        numero: lectura.numeroLote,
        clienteId: String(lectura.alumno._id),
      });
    });

    const veredictos = new Array(lecturas.length).fill(null);

    for (const bloque of partirEnBloques(pendientes, LOTES_POR_CONSULTA)) {
      let res;
      try {
        res = await axios.post(
          "ventasLibrosDigitales/resolverLotesVentasLibrosDigitales",
          {
            empresaId: this.props.empresaId,
            clienteOrigenId: this.state.clienteOrigenId,
            lotes: bloque.map((valorPendiente) => ({
              numero: valorPendiente.numero,
              clienteId: valorPendiente.clienteId,
            })),
          },
          { headers: this.props.headersToken },
        );
      } catch (err) {
        message.error("No se pudo consultar el estado de los códigos");
        return null;
      }

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo consultar el estado de los códigos");
        return null;
      }

      (res.data.resultados || []).forEach((valorResultado, indiceEnBloque) => {
        const pendiente = bloque[indiceEnBloque];
        if (pendiente) veredictos[pendiente.posicion] = valorResultado;
      });
    }

    return veredictos;
  };

  /**
   * Pasada previa sobre el archivo completo, antes de validar fila por fila.
   *
   * Es `async` a propósito y el importador genérico la espera: aquí se consulta al backend
   * el estado de cada código, y sin eso la vista previa no podría decir qué va a pasar. Es
   * además el único lugar desde donde se pueden tomar decisiones que dependen de OTRAS filas
   * (deduplicar códigos repetidos y agrupar los códigos de un mismo alumno).
   */
  prepararContextoArchivo = async (hojas, catalogos) => {
    const filas = hojas?.[0]?.data || [];

    const { columnas, faltantes, etiquetasFaltantes } = detectarColumnas(filas);

    const indices = construirIndicesAlumnos(catalogos?.alumnos);
    const lecturas = lecturaDeFilas({
      filas,
      columnas,
      indices,
      buscarAlumno: buscarAlumnoPorMatricula,
    });

    let bloqueado = null;

    if (faltantes.length > 0) {
      bloqueado = `No se encontraron las columnas obligatorias: ${etiquetasFaltantes}`;
      message.error(bloqueado);
    } else if (!this.state.clienteOrigenId) {
      // Sin este dato no se puede distinguir "la venta es del cliente de origen y hay que
      // pasársela al alumno" de "la venta es de otro cliente y no se toca", que es la decisión
      // central de toda la pantalla.
      bloqueado = "Selecciona el cliente de origen y vuelve a subir el archivo";
      message.error(bloqueado);
    }

    let veredictos = [];

    if (!bloqueado) {
      veredictos = await this.resolverLotes(lecturas);
      if (!veredictos) {
        bloqueado = "No se pudo consultar el estado de los códigos; vuelve a subir el archivo";
        veredictos = [];
      }
    }

    const decisiones = analizarArchivo({ lecturas, veredictos, bloqueado });

    const resumen = {};
    decisiones.forEach((valorDecision) => {
      const estado = valorDecision.datos?.estadoPrevisto || "error";
      resumen[estado] = (resumen[estado] || 0) + 1;
    });

    // Incluye las filas con error: en el Excel también se tienen que ver.
    const vistaPrevia = [...decisiones.values()]
      .map((valueDecision) => valueDecision.datos)
      .filter(Boolean)
      .sort((a, b) => (Number(a.numeroFila) || 0) - (Number(b.numeroFila) || 0));

    this.setState({ resumen, vistaPrevia });

    return { decisiones };
  };

  validarFila = (fila, catalogos, indice, contexto) =>
    contexto?.decisiones?.get(indice) || {
      ok: false,
      errores: [{ campo: "Fila", motivo: "No se pudo analizar" }],
    };

  columnasPreview = () => [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 120 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 190 },
    { title: "Código", dataIndex: "numeroLote", key: "numeroLote", width: 150 },
    {
      title: "Qué va a pasar",
      key: "estadoPrevisto",
      width: 180,
      render: (_, record) => (
        <>
          <Tag color={colorEstado(record.estadoPrevisto)}>
            {etiquetaEstado(record.estadoPrevisto)}
          </Tag>
          {record.librosAgrupados > 1 && (
            <Tooltip title="Los códigos de este alumno se procesan juntos y quedan en una sola venta">
              <Tag color="purple">{record.librosAgrupados} libros</Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    { title: "Libro", dataIndex: "insumo", key: "insumo", width: 200 },
    {
      title: "Venta encontrada",
      key: "folio",
      width: 210,
      render: (_, record) => {
        if (!record.folio) return "";
        return (
          <>
            <div>{record.folio}</div>
            {record.clienteVenta && (
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.clienteVenta}</div>
            )}
          </>
        );
      },
    },
    {
      title: "Se envía a",
      key: "destinatarios",
      width: 230,
      render: (_, record) => (
        <>
          {record.destinatarios || <Tag color="orange">Sin correo</Tag>}
          {record.correoNuevo && (
            <Tooltip title="El correo del archivo no está en la ficha del alumno; se le va a guardar al importar.">
              <Tag color="blue" style={{ marginLeft: 6 }}>
                Correo nuevo
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 300 },
  ];

  antesDeImportar = () => {
    const { clienteOrigenId, proyectoId, bancoId, formaDePagoId } = this.state;

    if (!clienteOrigenId || !proyectoId || !bancoId || !formaDePagoId) {
      message.warning(
        "Selecciona el cliente de origen, el Proyecto, el Banco y la Forma de Pago antes de importar",
      );
      return false;
    }

    return true;
  };

  prepararPayloadExtra = () => ({
    clienteOrigenId: this.state.clienteOrigenId,
    proyectoId: this.state.proyectoId,
    bancoId: this.state.bancoId,
    formaDePagoId: this.state.formaDePagoId,
  });

  // Después de una importación se sale de la pantalla, como antes: la vista previa del archivo
  // sigue cargada y volver a darle Importar lo procesaría otra vez (sin duplicar ventas, pero
  // reenviando los correos). Si sólo se estaban viendo los resultados de una corrida anterior,
  // se queda en la pantalla.
  cerrarModal = () => {
    const eraCorridaAnterior = this.state.verCorridaAnterior;
    this.setState({ modalVisible: false, resultado: null, verCorridaAnterior: false });
    if (!eraCorridaAnterior) this.irAtras();
  };

  verResultadoCorridaAnterior = async () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return;
    this.setState({ verCorridaAnterior: true });
    await this.cargarResultado(ultimaCorrida.corridaId);
  };

  renderCorridaAnterior = () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return null;

    const cuando = ultimaCorrida.fin || ultimaCorrida.inicio;
    const fecha = cuando ? new Date(cuando).toLocaleString("es-MX") : "";
    const como = ultimaCorrida.interrumpida || !ultimaCorrida.terminado
      ? "se interrumpió antes de terminar"
      : ultimaCorrida.detenida
        ? "se detuvo antes de terminar"
        : "terminó";

    return (
      <Alert
        type={ultimaCorrida.terminado && !ultimaCorrida.interrumpida && !ultimaCorrida.detenida ? "success" : "warning"}
        showIcon
        style={{ marginBottom: 12 }}
        message={`Tu última importación (${fecha}) ${como}`}
        description="Puedes ver y descargar sus resultados. Si quedó incompleta, vuelve a subir el mismo archivo: sólo se procesa lo que faltó."
        action={
          <Button size="small" onClick={this.verResultadoCorridaAnterior}>
            Ver resultados
          </Button>
        }
      />
    );
  };

  renderResumen = () => {
    const { resumen } = this.state;
    if (!resumen) return null;

    const entradas = Object.entries(resumen).filter(([, cantidad]) => cantidad > 0);
    if (entradas.length === 0) return null;

    return (
      <div style={{ marginBottom: 12 }}>
        <Space size={[8, 8]} wrap>
          {entradas.map(([estado, cantidad]) => (
            <Tag color={colorEstado(estado)} key={estado}>
              {`${cantidad} · ${etiquetaEstado(estado)}`}
            </Tag>
          ))}
          {this.state.vistaPrevia.length > 0 && (
            <Button size="small" icon={<DownloadOutlined />} onClick={this.descargarVistaPrevia}>
              Descargar vista previa
            </Button>
          )}
        </Space>
      </div>
    );
  };

  descargarVistaPrevia = () => {
    try {
      descargarVistaPrevia(this.state.vistaPrevia);
    } catch (err) {
      console.log(err);
      message.error("No se pudo generar el Excel de la vista previa");
    }
  };

  controlesExtra = () => {
    const { catalogos, clienteOrigenId, proyectoId, bancoId, formaDePagoId } = this.state;

    const bancos = catalogos?.bancos || [];
    const monedas = catalogos?.monedas || [];
    const bancoElegido = bancos.find((banco) => String(banco._id) === String(bancoId));
    const monedaBanco = monedas.find(
      (moneda) => String(moneda._id) === String(bancoElegido?.monedaId),
    );

    return (
      <>
        {this.renderCorridaAnterior()}

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Elige el cliente de origen antes de subir el archivo"
          description="Es el cliente con el que se registraron las ventas de los códigos (normalmente Público en General). Sólo se tocan las ventas que estén a su nombre: si una trae sólo los códigos del alumno se le cambia el cliente completa, y si trae códigos de otros alumnos los del alumno se separan a una venta propia. Las ventas de cualquier otro cliente se reportan y no se tocan. Las ventas de códigos que todavía no se vendieron se registran como remisión de contado en el banco que elijas, con un renglón por cada código del alumno."
        />

        <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={6}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Cliente de origen</div>
            <SelectClientesItem
              style={{ width: "100%" }}
              placeholder="Cliente con el que se registraron las ventas"
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              value={clienteOrigenId}
              onChange={(valor) => this.setState({ clienteOrigenId: valor })}
            />
          </Col>
          <Col xs={24} md={6}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Proyecto</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Proyecto"
              value={proyectoId}
              onChange={(valor) => this.setState({ proyectoId: valor })}
            >
              {(catalogos?.proyectos || []).map((proyecto) => (
                <Select.Option value={String(proyecto._id)} key={String(proyecto._id)}>
                  {proyecto.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={6}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Banco {monedaBanco ? `(${monedaBanco.abreviacion})` : ""}
            </div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Banco"
              value={bancoId}
              onChange={(valor) => this.setState({ bancoId: valor })}
            >
              {bancos.map((banco) => (
                <Select.Option value={String(banco._id)} key={String(banco._id)}>
                  {banco.nombre}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
          <Col xs={24} md={6}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Forma de Pago</div>
            <SelectItem
              style={{ width: "100%" }}
              placeholder="Forma de Pago"
              value={formaDePagoId}
              onChange={(valor) => this.setState({ formaDePagoId: valor })}
            >
              {(catalogos?.formasDePago || []).map((forma) => (
                <Select.Option value={String(forma._id)} key={String(forma._id)}>
                  {forma.c_FormaPago + " - " + forma.descripcion}
                </Select.Option>
              ))}
            </SelectItem>
          </Col>
        </Row>

        {this.renderResumen()}
      </>
    );
  };

  render() {
    return (
      <>
        <ImportadorExcelGenerico
          {...this.props}
          titulo="Importar Ventas de Libros Digitales"
          nombreEntidadPlural="códigos"
          endpointDatosImportar="ventasLibrosDigitales/datosParaImportarVentasLibrosDigitales"
          endpointImportar="ventasLibrosDigitales/iniciarImportacionVentasLibrosDigitales"
          nombrePlantilla={NOMBRE_PLANTILLA}
          construirPlantilla={construirPlantilla}
          prepararCatalogos={this.prepararCatalogos}
          prepararContextoArchivo={this.prepararContextoArchivo}
          validarFila={this.validarFila}
          columnasPreview={this.columnasPreview()}
          instrucciones={instrucciones}
          controlesExtra={this.controlesExtra()}
          antesDeImportar={this.antesDeImportar}
          prepararPayloadExtra={this.prepararPayloadExtra}
          // La importación corre en el servidor: la pantalla manda el archivo una sola vez,
          // sigue el avance (ModalProgreso) y al final pide los resultados.
          enviarImportacion={this.enviarImportacion}
          // Todas las filas procesables se mandan como "crear": el desenlace real de cada una
          // lo decide el backend y se ve en la columna "Qué va a pasar". Así las que sólo se
          // reportan (de otro cliente, timbradas, en global) también llegan al Excel de
          // resultados, que es justo para lo que sirve.
          etiquetasAccion={{
            crear: "Procesar",
            editar: null,
            sinCambios: "Omitir",
            error: ESTADOS.error.etiqueta,
          }}
          permitirEdicion
        />

        <ModalProgreso
          visible={this.state.progresoVisible}
          titulo="Importando ventas de libros digitales"
          descripcion="Corre en el servidor, un renglón tras otro sin saturarlo. Puedes salir: sigue sola y al volver a esta pantalla ves su avance."
          procesados={this.state.progreso?.procesados || 0}
          total={this.state.progreso?.total ?? null}
          detalle={this.state.progreso?.detalle || null}
          unidad="renglones"
          onDetener={this.detenerImportacion}
          deteniendo={this.state.deteniendo}
          onOcultar={this.salirSinDetener}
        />

        <ResultadosLibrosDigitales
          visible={this.state.modalVisible}
          resultado={this.state.resultado}
          cerrarModal={this.cerrarModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  empresaId: state.empresa.id,
  headersToken: state.user.headersToken,
});

export default connect(mapStateToProps)(ImportarVentasLibrosDigitales);
